import {
  type DateField,
  type DateTimeFormatPreference,
  localeUses12Hour,
  NUMERIC_DATE_PATTERNS,
  NUMERIC_FIELDS_OPTIONS,
} from "./format";

/**
 * How a date picker's text field is written and typed under a preference, in
 * the vocabulary MUI X's Day.js adapter takes.
 */
export interface DatePickerFormat {
  /**
   * The Day.js pattern of a typed date (e.g. `DD.MM.YYYY`), which the
   * picker's field uses for its date section and follows with the time.
   */
  keyboardDate: string;
  /** Whether time is typed on a 12-hour clock with an AM/PM section. */
  ampm: boolean;
}

/** Each date field as a padded Day.js token, so every section has a fixed width to type into. */
const DAYJS_TOKENS: Record<DateField, string> = {
  year: "YYYY",
  month: "MM",
  day: "DD",
};

function isDateField(type: Intl.DateTimeFormatPartTypes): type is DateField {
  return type === "year" || type === "month" || type === "day";
}

/**
 * A non-field part as Day.js literal text. A part with letters (e.g. the
 * ` г.` Bulgarian closes a date with) is bracketed, or Day.js would read its
 * letters as tokens; bare separators stay as they are.
 */
function dayjsLiteral(value: string): string {
  return /\p{L}/u.test(value) ? `[${value}]` : value;
}

/**
 * The numeric Gregorian date pattern `locale` writes (the browser's when
 * omitted), with padded fields. Gregorian because that is the only calendar
 * the picker types; a locale that defaults to another (Persian, Buddhist)
 * would otherwise hand back that calendar's order and era. Only the order and
 * separators of the formatted parts matter, so any sample date works.
 */
function localeKeyboardDate(locale: string | undefined): string {
  return new Intl.DateTimeFormat(locale, {
    ...NUMERIC_FIELDS_OPTIONS,
    calendar: "gregory",
  })
    .formatToParts(new Date(2026, 5, 30))
    .map((part) =>
      isDateField(part.type)
        ? DAYJS_TOKENS[part.type]
        : dayjsLiteral(part.value),
    )
    .join("");
}

/**
 * The picker format matching how `preference` writes dates elsewhere:
 *
 * - A fixed preset (`iso`, `swiss`, `us`, `uk`) types in its own order and
 *   separator.
 * - `system` and `long` type in `locale`'s numeric order (the browser's when
 *   omitted); `long`'s spelled-out month has no typeable form.
 * - A `system` clock follows `locale`'s 12/24-hour convention.
 */
export function datePickerFormat(
  preference: DateTimeFormatPreference,
  locale?: string,
): DatePickerFormat {
  const { datePreset, clock } = preference;
  const ampm = clock === "system" ? localeUses12Hour(locale) : clock === "12h";
  if (datePreset === "long" || datePreset === "system") {
    return { keyboardDate: localeKeyboardDate(locale), ampm };
  }
  const { order, separator } = NUMERIC_DATE_PATTERNS[datePreset];
  const keyboardDate = order
    .map((field) => DAYJS_TOKENS[field])
    .join(separator);
  return { keyboardDate, ampm };
}
